import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { flushSync } from "react-dom";
import { projectBoardsList, projectBoardItemsAdd } from "./api";
import { FlyoutManager } from "./flyoutManager";
import { ProjectBoardForm } from "./projectboardform";

let root = null;

export function AddToBoard({ itemId }) {
    const [boards, setBoards] = useState([]);
    const [selectedBoards, setSelectedBoards] = useState([]);
    const [successBoards, setSuccessBoards] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        fetchBoards();
    }, []);

    useEffect(() => {
        const handleFlyoutClosed = (event) => {
            if (event?.detail?.id !== "addToBoard") {
                return;
            }

            setSuccessBoards([]);
            setErrorMessage(null);
        };

        document.addEventListener("flyout:closed", handleFlyoutClosed);
        return () => {
            document.removeEventListener("flyout:closed", handleFlyoutClosed);
        };
    }, []);

    async function fetchBoards() {
        try {
            const data = await projectBoardsList();
            setBoards(data.items);
        } catch (err) {
            console.error("Failed to load boards", err);
            setBoards([]);
        }
    }

    function handleToggle(boardId) {
        setSelectedBoards(prev =>
            prev.includes(boardId) ? prev.filter(id => id !== boardId) : [...prev, boardId]
        );
    }

    async function handleAddToBoards() {
        const results = await projectBoardItemsAdd(selectedBoards, itemId);

        const failed = results.filter(r => !r.success).map(r => r.boardId);
        if (failed.length > 0) {
            setErrorMessage(`Failed to add item to ${failed.length} board(s). Please try again.`);
        } else {
            setErrorMessage(null);
            setSuccessBoards(selectedBoards);
            setSelectedBoards([]);
        }
    }

    function handleBoardSaved(newBoardId) {
        fetchBoards().then(() => {
            setShowForm(false);
            setSelectedBoards([newBoardId]);
        });
    }

    return (
        <div className="cmp-add-to-board">
            {showForm ? (
                <div>
                    <a
                        href="#"
                        className="cmp-add-to-board__back"
                        onClick={(e) => { e.preventDefault(); setShowForm(false); }}
                    >
                        <img src="/images/arrow-left.svg" alt="Back" className="cmp-add-to-board__back-icon" />
                        Back to Add to Project Board
                    </a>
                    <ProjectBoardForm onBoardSaved={handleBoardSaved} />
                </div>
            ) : (
                <>
                    <h4 className="cmp-add-to-board__title">Add to Project Board</h4>
                    <p className="cmp-add-to-board__subtitle">Select the board(s) to add this style:</p>

                    <div className="cmp-add-to-board__select-all">
                        <label className="cmp-add-to-board__select-all-label">
                            <input
                                type="checkbox"
                                checked={selectedBoards.length === boards.length && boards.length > 0}
                                onChange={() => {
                                    if (selectedBoards.length === boards.length) {
                                        setSelectedBoards([]);
                                    } else {
                                        setSelectedBoards(boards.map(b => b.id));
                                    }
                                }}
                            />
                            Select all
                        </label>
                    </div>

                    <ul className="cmp-add-to-board__list">
                        {boards.map(board => (
                            <li key={board.id} className="cmp-add-to-board__item">
                                <label className="cmp-add-to-board__label">
                                    <input
                                        type="checkbox"
                                        checked={selectedBoards.includes(board.id)}
                                        onChange={() => handleToggle(board.id)}
                                    />
                                    <div className="cmp-add-to-board__board">
                                        {board.coverImageUrl && (
                                            <img
                                                src={board.coverImageUrl}
                                                alt={board.name}
                                                className="cmp-add-to-board__thumbnail"
                                            />
                                        )}
                                        <div className="cmp-add-to-board__info">
                                            {board.publicUrl 
                                                ? (<a href={board.publicUrl} target="_blank"><span className="cmp-add-to-board__name">{board.name}</span></a>)
                                                : (<span className="cmp-add-to-board__name">{board.name}</span>)}
                                            
                                            <span className="cmp-add-to-board__segments">
                                                {board.segmentTypes?.length
                                                    ? board.segmentTypes.map(st => st.name).join(", ")
                                                    : ""}
                                            </span>
                                        </div>
                                    </div>
                                </label>
                            </li>
                        ))}
                    </ul>

                    {successBoards.length > 0 && (
                        <div className="cmp-add-to-board__success">
                            Added to {successBoards.length > 1 ? "boards" : "board"} successfully!
                        </div>
                    )}
                    {errorMessage && (
                        <div className="cmp-add-to-board__error">
                            {errorMessage}
                        </div>
                    )}

                    <div className="cmp-add-to-board__actions">
                        <button
                            className="btn"
                            onClick={handleAddToBoards}
                            disabled={selectedBoards.length === 0}
                        >
                            Add To Board
                        </button>
                        <button className="btn-secondary" onClick={() => FlyoutManager.close("addToBoard")}>
                            Cancel
                        </button>
                    </div>

                    <hr className="cmp-add-to-board__divider" />

                    <div className="cmp-add-to-board__create">
                        <p className="cmp-add-to-board__create-missing">Missing a board?</p>
                        <button className="btn-secondary" onClick={() => setShowForm(true)}>
                            Create a New Project Board
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export function openAddToBoard(assetInfo, trigger = null) {
    if (!root) {
        console.error("AddToBoard root not found");
        return;
    }
    
    const isAuthenticated = window.mannington.isAuthenticated === true;

    if (isAuthenticated) {
        flushSync(() => {
            root.render(<AddToBoard itemId={assetInfo} />);
        });
        FlyoutManager.open("addToBoard", trigger);
    } else {
        FlyoutManager.open("SignInForm", trigger);
    }
}

/**
 /**
 * Attaches event handlers to "Add to Board" flyout triggers.
 *
 * Finds all buttons with `data-flyout-trigger="addToBoard"` and adds click handlers.
 * The `assetInfo` is resolved by first checking the button's `data-asset-info` attribute.
 * If not found, it looks for an `img.add-to-board` element with `data-mc-project-board-asset-info`
 * inside the closest `.add-to-board-wrapper` ancestor.
 *
 * @return {void} This method does not return any value.
 */
export function initAddToBoard() {
    const container = document.getElementById("add-to-board-root");
    if (!container) return;
    
    root = ReactDOM.createRoot(container);

    const buttons = document.querySelectorAll('[data-flyout-trigger="addToBoard"]');
    buttons.forEach(button => {
        button.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Try to read data-asset-info directly from the button
            let assetInfo = button.getAttribute("data-asset-info");

            // Fallback: look inside the wrapper for the add-to-board image
            if (!assetInfo) {
                const wrapper = button.closest(".add-to-board-wrapper");
                if (!wrapper) return;

                const imgEl = wrapper.querySelector("img.add-to-board[data-mc-project-board-asset-info]");
                if (!imgEl) return;

                assetInfo = imgEl.getAttribute("data-mc-project-board-asset-info");
            }

            if (!assetInfo) return;
            openAddToBoard(assetInfo, button);
        }, true);
    });
}
